import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ProjectFilterProvider } from "@/lib/projectFilter";
import { AccessProvider } from "@/lib/access";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppShell from "@/components/AppShell";
import DashboardPage from "@/pages/DashboardPage";
// 2026-06-11: TasksPage tidak lagi di-mount langsung dari App.js — sudah
// diintegrasikan ke MyWorkPage sebagai view "Kanban Task". Route /tasks
// redirect ke /my-work?view=kanban di bawah.
import MyWorkPage from "@/pages/MyWorkPage";
import RemindersPage from "@/pages/RemindersPage";
import RemindersRedirect from "@/pages/RemindersRedirect";
import InfoAgendaPage from "@/pages/InfoAgendaPage";
import AssetsPage from "@/pages/AssetsPage";
import PerformancePage from "@/pages/PerformancePage";
import ProjectsPage from "@/pages/ProjectsPage";
import MonitoringProjectPage from "@/pages/MonitoringProjectPage";
import NewProjectPage from "@/pages/NewProjectPage";
import TeamPage from "@/pages/TeamPage";
import SettingsPage from "@/pages/SettingsPage";
import IdleTimeoutProvider from "@/components/IdleTimeoutProvider";
import UserManagementPage from "@/pages/UserManagementPage";
import LeaveCalendarPage from "@/pages/LeaveCalendarPage";
import AttendancePage from "@/pages/AttendancePage";
import PettyCashPage from "@/pages/PettyCashPage";
// 2026-06-20 (user request): Kas & Bank menaungi Weekly Petty Cash + Buku Bank.
import KasBankPage, { PettyCashLegacyRedirect } from "@/pages/KasBankPage";
import BankLedgerPage from "@/pages/BankLedgerPage";
import AuditLogPage from "@/pages/AuditLogPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
// 2026-06-11: DataIntegrity, CriticalIssues, DecisionLog, MessagingAudit,
// OverheadReport, Collection routes tetap ada untuk backward-compat bookmark
// dan drill-in dari halaman lain, tapi tidak lagi punya menu sidebar terpisah.
import DataIntegrityPage from "@/pages/DataIntegrityPage";
import CollectionPage from "@/pages/CollectionPage";
import FinanceReportPage from "@/pages/FinanceReportPage";
import RekonsiliasiArPage from "@/pages/RekonsiliasiArPage";
import RekonsiliasiKasStafPage from "@/pages/RekonsiliasiKasStafPage";
import PenetapanCoaPage from "@/pages/PenetapanCoaPage";
// 2026-09-22 (user request): drill-down pengeluaran — Detail Pengeluaran per
// project (grup mingguan) → Detail Pengeluaran Mingguan (rincian periode).
import PengeluaranProjectPage from "@/pages/PengeluaranProjectPage";
import PengeluaranMingguPage from "@/pages/PengeluaranMingguPage";
import CostControlPage from "@/pages/CostControlPage";
import ShadowReconciliationPage from "@/pages/ShadowReconciliationPage";
import PasswordResetRequestsPage from "@/pages/PasswordResetRequestsPage";
// 2026-06-11 (TENDER-SUNSET): fitur Proses Tender dihapus permanen. Data historis
// tetap aman di /app/backups/tender_backup_2026-06-11.json (audit trail).
import CriticalIssuesPage from "@/pages/CriticalIssuesPage";
import DecisionLogPage from "@/pages/DecisionLogPage";
import MessagingAuditPage from "@/pages/MessagingAuditPage";
import FinanceMasterPage from "@/pages/FinanceMasterPage";
import OverheadReportPage from "@/pages/OverheadReportPage";
// 2026-02-13 (user request): pengaturan Akses Audit Log (override scope per user).
import SettingsAuditAccessPage from "@/pages/SettingsAuditAccessPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// 2026-02-13 (bug fix, user report): redirect /tasks → /my-work?view=kanban
// WAJIB meneruskan search params (task/comment) — deep-link "Buka Detail" dari
// Pusat Pekerjaan Saya & notifikasi membawa ?task=<id> untuk auto-open dialog
// detail di papan Kanban. Tanpa ini user harus mencari task secara manual.
function TasksRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("view", "kanban");
  return <Navigate to={`/my-work?${params.toString()}`} replace />;
}

// R232 (2026-10-20, user request): /leaves digabung ke halaman Kehadiran &
// Perizinan (tab "Cuti & Izin"). Redirect meneruskan seluruh query param
// (mis. ?open=<id> deep-link dari Pusat Pekerjaan) agar bookmark lama hidup.
function LeavesRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("tab", "cuti_izin");
  return <Navigate to={`/attendance?${params.toString()}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="bottom-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route element={<Protected><AccessProvider><ProjectFilterProvider><IdleTimeoutProvider><AppShell /></IdleTimeoutProvider></ProjectFilterProvider></AccessProvider></Protected>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              {/* 2026-06-11 (user request): merge Tasks → My Work & Tasks.
                  /tasks tetap redirect ke /my-work?view=kanban supaya bookmark
                  lama tidak broken. 2026-02-13 (bug fix): param task/comment
                  diteruskan via TasksRedirect. */}
              <Route path="/tasks" element={<TasksRedirect />} />
              <Route path="/my-work" element={<MyWorkPage />} />
              <Route path="/reminders" element={<RemindersRedirect />} />
              <Route path="/announcements" element={<Navigate to="/info-agenda?tab=pengumuman" replace />} />
              <Route path="/info-agenda" element={<InfoAgendaPage />} />
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/performance" element={<PerformancePage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/new" element={<NewProjectPage />} />
              <Route path="/projects/:pid/monitoring" element={<MonitoringProjectPage />} />
              <Route path="/projects/:pid/cost-control" element={<CostControlPage />} />
              <Route path="/admin/shadow-report" element={<ShadowReconciliationPage />} />
              <Route path="/user-management/password-reset-requests" element={<PasswordResetRequestsPage />} />
              <Route path="/collection" element={<CollectionPage />} />
              <Route path="/finance-report" element={<FinanceReportPage />} />
              <Route path="/finance-report/rekonsiliasi-ar" element={<RekonsiliasiArPage />} />
              <Route path="/finance-report/rekonsiliasi-kas-staf" element={<RekonsiliasiKasStafPage />} />
              <Route path="/petty-cash/penetapan-coa" element={<PenetapanCoaPage />} />
              <Route path="/finance-report/overhead" element={<OverheadReportPage />} />
              <Route path="/finance-report/pengeluaran/:pid/minggu/:week" element={<PengeluaranMingguPage />} />
              <Route path="/finance-report/pengeluaran/:pid" element={<PengeluaranProjectPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/finance-master" element={<FinanceMasterPage />} />
              {/* R232 (2026-10-20): /leaves digabung ke halaman Kehadiran &
                  Perizinan (tab "Cuti & Izin") — redirect menjaga bookmark &
                  tautan lama (param `open` diteruskan). /leaves/calendar tetap
                  aktif sebagai sub-tampilan kalender dari tab tersebut. */}
              <Route path="/leaves" element={<LeavesRedirect />} />
              <Route path="/leaves/calendar" element={<LeaveCalendarPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/kas-bank/ledger/:accountId" element={<BankLedgerPage />} />
              <Route path="/kas-bank" element={<KasBankPage />} />
              <Route path="/petty-cash" element={<PettyCashLegacyRedirect />} />
              <Route path="/admin/users" element={<UserManagementPage />} />
              <Route path="/admin/audit" element={<AuditLogPage />} />
              <Route path="/settings/audit-access" element={<SettingsAuditAccessPage />} />
              <Route path="/admin/critical-issues" element={<CriticalIssuesPage />} />
              <Route path="/admin/decision-log" element={<DecisionLogPage />} />
              <Route path="/admin/messaging-audit" element={<MessagingAuditPage />} />
              <Route path="/admin/data-integrity" element={<DataIntegrityPage />} />
              {/* 2026-09-25: catch-all — URL tak dikenal (termasuk bookmark fitur
                  yang sudah dihapus seperti /tenders & /accurate-update) kembali
                  ke dashboard, bukan halaman putih kosong. */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
